import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  TestSuite,
  TestExecutionDocument,
  SignatureMatch,
  UnmatchedSubject,
  compareTestDataSignature,
  unmatchedSubjects,
} from '@finvari/deliver';
import { getBaselineCandidates } from '../../../firebase';
import { formatSignature } from './formatSignature';

interface TestSubjectSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  testSuite: TestSuite;
  environmentId: string;
  onConfirm: (
    selectedSubjects: string[],
    baselineExecutionId?: string,
    comments?: string,
    acknowledgedDataMismatch?: boolean,
  ) => void;
  // The suite's pinned golden baseline execution id, if configured. Used as the preferred default so
  // successive runs of a PR compare against the same reference; a reviewer choosing anything else is
  // warned that cross-run diffs will differ.
  goldenBaselineId?: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// A one-line "subject (baseline X vs environment Y)" for the mismatch alert. A missing signature on
// either side reads as "no signature" rather than being hidden, since an absent signature is itself
// the reason the subject is unmatched.
function describeUnmatched(u: UnmatchedSubject): string {
  const baseline = u.baselineSig ? formatSignature(u.baselineSig) : 'no signature';
  const env = u.envSig ? formatSignature(u.envSig) : 'no signature';
  return `${u.subject} (baseline ${baseline} vs environment ${env})`;
}

function countSignatureMatches(
  rolloutSig: Record<string, string> | undefined,
  candidateSig: Record<string, string> | undefined
): number {
  if (!rolloutSig || !candidateSig) return 0;
  return Object.keys(rolloutSig).filter(
    subject => candidateSig[subject] !== undefined && rolloutSig[subject] === candidateSig[subject]
  ).length;
}

export default function TestSubjectSelectionDialog({
                                                     open,
                                                     onClose,
                                                     testSuite,
                                                     environmentId,
                                                     onConfirm,
                                                     goldenBaselineId,
                                                   }: TestSubjectSelectionDialogProps) {
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [showError, setShowError] = useState(false);
  const [filter, setFilter] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [comments, setComments] = useState('');
  // Explicit acknowledgement that the run may compare against mismatched data. Load-bearing: the
  // server rejects an unacknowledged mismatched run. Reset on any change to the comparison (below) so
  // an acknowledgement of one subject/baseline set can never silently carry into a different one.
  const [acknowledgedMismatch, setAcknowledgedMismatch] = useState(false);

  // Baseline selection state
  const [baselineCandidates, setBaselineCandidates] = useState<TestExecutionDocument[]>([]);
  const [selectedBaselineId, setSelectedBaselineId] = useState<string>('');
  // The baseline the dialog auto-chose (golden if configured, else best signature match). Choosing a
  // different one warns that comparisons across runs of this PR will not be apples-to-apples.
  const [defaultBaselineId, setDefaultBaselineId] = useState<string>('');
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineError, setBaselineError] = useState<string | null>(null);
  const [showAllBaselines, setShowAllBaselines] = useState(false);

  // Filter baselines: by default, exclude runs from the same environment and runs that had a baseline set
  const filteredBaselines = useMemo(() => {
    if (showAllBaselines) return baselineCandidates;
    return baselineCandidates.filter(c =>
      c.environment !== environmentId && !c.baselineExecutionId
    );
  }, [baselineCandidates, environmentId, showAllBaselines]);

  // Derive the selected baseline's subjects (null when no baseline selected)
  const selectedBaseline = useMemo(() => {
    if (!selectedBaselineId) return null;
    return baselineCandidates.find(c => c.executionId === selectedBaselineId) ?? null;
  }, [selectedBaselineId, baselineCandidates]);

  const baselineSubjects = useMemo(() => {
    return selectedBaseline ? new Set(selectedBaseline.subjects) : null;
  }, [selectedBaseline]);

  // When baseline changes, prune selected subjects to those in the baseline
  useEffect(() => {
    if (baselineSubjects) {
      setSelectedSubjects(prev => prev.filter(s => baselineSubjects.has(s)));
    }
  }, [baselineSubjects]);

  // Initialize with all subjects selected by default
  useEffect(() => {
    if (open) {
      setSelectedSubjects(testSuite.defaultSubjects);
      setShowError(false);
      setFilter('');
      setShowFilter(false);
      setComments('');
      setAcknowledgedMismatch(false);
      setSelectedBaselineId('');
      setDefaultBaselineId('');
      setBaselineError(null);
      setShowAllBaselines(false);

      // Fetch baseline candidates if needed
      if (testSuite.needsBaseline) {
        setBaselineLoading(true);
        getBaselineCandidates(testSuite.id)
          .then(candidates => {
            setBaselineCandidates(candidates);
            // Filter to valid baselines (different env, no baseline of its own)
            const validCandidates = candidates.filter(c =>
              c.environment !== environmentId && !c.baselineExecutionId
            );
            // Prefer the pinned golden baseline so runs stay comparable across the PR. Fall back to
            // the best signature match, then to the most recent valid candidate.
            const golden = goldenBaselineId
              ? candidates.find(c => c.executionId === goldenBaselineId)
              : undefined;
            let defaultCandidate = golden ?? validCandidates[0]; // most recent (sorted desc)
            if (!golden && testSuite.testDataSignatures && validCandidates.length > 1) {
              let bestCount = 0;
              for (const c of validCandidates) {
                const count = countSignatureMatches(testSuite.testDataSignatures, c.testDataSignatures);
                if (count > bestCount) {
                  bestCount = count;
                  defaultCandidate = c;
                }
              }
            }
            if (defaultCandidate) {
              setSelectedBaselineId(defaultCandidate.executionId);
              setDefaultBaselineId(defaultCandidate.executionId);
              // A golden baseline may be filtered out of the default dropdown (e.g. same env); make
              // sure it's visible so the reviewer can see what they're comparing against.
              if (golden && (golden.environment === environmentId || golden.baselineExecutionId)) {
                setShowAllBaselines(true);
              }
              // Filter default subjects to those in the baseline
              if (defaultCandidate.subjects) {
                const baselineSet = new Set(defaultCandidate.subjects);
                setSelectedSubjects(testSuite.defaultSubjects.filter(s => baselineSet.has(s)));
              }
            }
            setBaselineLoading(false);
          })
          .catch(err => {
            console.error('Failed to fetch baseline candidates:', err);
            setBaselineError('Failed to load baseline candidates');
            setBaselineLoading(false);
          });
      }
    }
  }, [open, testSuite.defaultSubjects, testSuite.needsBaseline, testSuite.id, testSuite.testDataSignatures, environmentId, goldenBaselineId]);

  const handleToggleSubject = (subject: string) => {
    setSelectedSubjects(prev => {
      if (prev.includes(subject)) {
        return prev.filter(s => s !== subject);
      } else {
        return [...prev, subject];
      }
    });
    setShowError(false);
  };

  const filteredSubjects = useMemo(() => {
    let subjects = testSuite.testSubjects;
    if (baselineSubjects) {
      subjects = subjects.filter(s => baselineSubjects.has(s));
    }
    if (filter) {
      if (filter.includes(',')) {
        const terms = filter.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);
        subjects = subjects.filter(s => terms.includes(s.toLowerCase()));
      } else {
        const lower = filter.toLowerCase();
        subjects = subjects.filter(s => s.toLowerCase().includes(lower));
      }
    }
    return subjects;
  }, [testSuite.testSubjects, baselineSubjects, filter]);

  const handleSelectAll = () => {
    setSelectedSubjects(prev => {
      const set = new Set(prev);
      filteredSubjects.forEach(s => set.add(s));
      return Array.from(set);
    });
    setShowError(false);
  };

  const handleDeselectAll = () => {
    if (filter) {
      const filtered = new Set(filteredSubjects);
      setSelectedSubjects(prev => prev.filter(s => !filtered.has(s)));
    } else {
      setSelectedSubjects([]);
    }
  };

  // The selected subjects whose data is not a confirmed match with the baseline (differing or absent
  // signatures). Only meaningful once a baseline is chosen — a baseline-less run has no comparison to
  // be wrong about, so it is never gated. Absent-on-either-side counts as unmatched ('unknown').
  const unmatched: UnmatchedSubject[] = useMemo(() => {
    if (!selectedBaselineId) return [];
    return unmatchedSubjects(
      selectedSubjects,
      testSuite.testDataSignatures,
      selectedBaseline?.testDataSignatures,
    );
  }, [selectedBaselineId, selectedSubjects, testSuite.testDataSignatures, selectedBaseline]);

  // Any change to what is being compared invalidates a prior acknowledgement.
  useEffect(() => {
    setAcknowledgedMismatch(false);
  }, [selectedBaselineId, selectedSubjects]);

  const mismatchBlocksRun = unmatched.length > 0 && !acknowledgedMismatch;

  const handleConfirm = () => {
    if (selectedSubjects.length === 0) {
      setShowError(true);
      return;
    }
    if (mismatchBlocksRun) return;
    // Baseline is optional - if not selected, we just won't pass it. Flag the run as an acknowledged
    // mismatch only when there actually is one, so a clean run never carries the flag (not even a
    // trailing `undefined` argument).
    if (unmatched.length > 0) {
      onConfirm(selectedSubjects, selectedBaselineId || undefined, comments.trim() || undefined, true);
    } else {
      onConfirm(selectedSubjects, selectedBaselineId || undefined, comments.trim() || undefined);
    }
    onClose();
  };

  const noneSelected = selectedSubjects.length === 0;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select test subjects</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {testSuite.name}: {testSuite.description}
        </Typography>

        {showError && selectedSubjects.length === 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Please select at least one subject to test.
          </Alert>
        )}

        <TextField
          size="small"
          label="Developer notes (optional)"
          placeholder="Optional context about this test run..."
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          sx={{ mb: 2 }}
        />

        {/* Baseline selection */}
        {testSuite.needsBaseline && (
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Baseline Test Run</InputLabel>
              <Select
                value={selectedBaselineId}
                onChange={(e) => setSelectedBaselineId(e.target.value)}
                label="Baseline Test Run"
                disabled={baselineLoading}
                startAdornment={baselineLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : undefined}
              >
                <MenuItem value="">
                  <em>Create a new baseline (no diff step will run)</em>
                </MenuItem>
                {filteredBaselines.map(candidate => {
                  const label = `${candidate.environment} - ${formatDate(candidate.createdAt)} - ${candidate.commitHash?.slice(0, 7) || 'unknown'}`;
                  const truncatedComments = candidate.comments
                    ? candidate.comments.length > 60 ? candidate.comments.slice(0, 57) + '...' : candidate.comments
                    : '';
                  return (
                    <MenuItem key={candidate.executionId} value={candidate.executionId}>
                      {label}{truncatedComments ? ` — ${truncatedComments}` : ''}
                    </MenuItem>
                  );
                })}
                {!baselineLoading && filteredBaselines.length === 0 && (
                  <MenuItem disabled value="_empty">
                    No baseline candidates available
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showAllBaselines}
                  onChange={(e) => setShowAllBaselines(e.target.checked)}
                />
              }
              label={<Typography variant="caption">Show all recent runs</Typography>}
              sx={{ mt: 0.5 }}
            />
            {baselineError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                {baselineError}
              </Typography>
            )}
            {/* The rename abandons every environment's old baselines, so the candidate list is empty
                until a fresh one is captured. The "No baseline candidates available" item is easy to
                miss inside a closed dropdown, so spell out what a baseline-less run means here. */}
            {!baselineLoading && !baselineError && filteredBaselines.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No baseline exists for this suite yet. This run will skip the diff step and become the
                baseline that later runs compare against.
              </Alert>
            )}
            {defaultBaselineId && selectedBaselineId && selectedBaselineId !== defaultBaselineId && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                You changed the baseline away from the {goldenBaselineId === defaultBaselineId ? 'golden' : 'recommended'} one.
                Diffs from this run won&apos;t line up with other runs of this PR that used the {goldenBaselineId === defaultBaselineId ? 'golden' : 'recommended'} baseline.
              </Alert>
            )}
          </Box>
        )}

        {showFilter && (
          <TextField
            size="small"
            placeholder="Filter subjects..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
            autoFocus
            sx={{ mb: 1 }}
          />
        )}

        <FormGroup sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {filteredSubjects.map(subject => {
            const sig = testSuite.testDataSignatures?.[subject];
            const baselineSig = selectedBaseline?.testDataSignatures?.[subject];
            // Only compare against a baseline when one is selected; without a baseline there is nothing
            // to be mismatched against, so every subject reads as a plain signature.
            const matchState: SignatureMatch = selectedBaselineId
              ? compareTestDataSignature(sig, baselineSig)
              : 'match';
            const isUnmatched = matchState !== 'match';
            let sigSuffix: React.ReactNode = null;
            if (matchState === 'mismatch') {
              sigSuffix = (
                <Tooltip title={`The baseline test ran with a snapshot from ${formatSignature(baselineSig!)}. This environment has a snapshot from ${formatSignature(sig!)}. This program's diff will be dominated by data drift, which can't be separated from your code change afterward — restore a fresh snapshot and/or pick a different baseline.`}>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}({formatSignature(baselineSig!)} ≠ {formatSignature(sig!)})
                  </Typography>
                </Tooltip>
              );
            } else if (matchState === 'unknown') {
              // One side has no signature, so we can't confirm the data matches — treated as unmatched.
              const known = sig ?? baselineSig;
              const missingSide = sig === undefined ? 'this environment' : 'the baseline';
              sigSuffix = (
                <Tooltip title={`We can't confirm this program's data matches the baseline: a snapshot signature is missing on ${missingSide}. Its diff may be dominated by data drift.`}>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}({known ? formatSignature(known) + ', ' : ''}data match unknown)
                  </Typography>
                </Tooltip>
              );
            } else if (sig) {
              sigSuffix = (
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}({formatSignature(sig)})
                </Typography>
              );
            }
            return (
              <FormControlLabel
                key={subject}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedSubjects.includes(subject)}
                    onChange={() => handleToggleSubject(subject)}
                  />
                }
                label={<Typography variant="body2" color={isUnmatched ? 'warning.main' : undefined}>{subject}{sigSuffix}</Typography>}
              />
            );
          })}
        </FormGroup>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {selectedSubjects.length} of {testSuite.testSubjects.length} subjects selected
          {selectedSubjects.length > 0 &&
            ` (${selectedSubjects.length * testSuite.testSteps.length} total test steps)`}
        </Typography>

        {unmatched.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {unmatched.length} of {selectedSubjects.length} selected program{selectedSubjects.length === 1 ? '' : 's'}{' '}
              {unmatched.length === 1 ? 'holds' : 'hold'} different data than the baseline:
            </Typography>
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {unmatched.map(u => (
                <li key={u.subject}>
                  <Typography component="span" variant="caption">
                    {describeUnmatched(u)}
                  </Typography>
                </li>
              ))}
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Those programs&apos; diffs will be dominated by data drift, and there is no way to
              separate that from changes in this pull request afterward.
            </Typography>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  size="small"
                  checked={acknowledgedMismatch}
                  onChange={e => setAcknowledgedMismatch(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Allow mismatched baseline data (pattern diffs might be very difficult to verify)
                </Typography>
              }
            />
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" variant="text" onClick={() => { setShowFilter(v => !v); if (showFilter) setFilter(''); }}>
          Search
        </Button>
        <Button size="small" variant="text" onClick={handleSelectAll} disabled={filteredSubjects.every(s => selectedSubjects.includes(s))}>
          Select All
        </Button>
        <Button size="small" variant="text" onClick={handleDeselectAll} disabled={filteredSubjects.every(s => !selectedSubjects.includes(s))}>
          Select None
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          disabled={noneSelected || mismatchBlocksRun}
        >
          Run Tests
        </Button>
      </DialogActions>
    </Dialog>
  );
}
